import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";

export type FinalPayableRoundingResult = {
  preRoundingTotal: number;
  roundingAmount: number;
  totalAmount: number;
  roundingScope: CommerceCalculationRuntimeItem["rounding_scope"];
  roundingMode: CommerceCalculationRuntimeItem["rounding_mode"];
  roundingIncrement: number;
  calculationSettingsVersionId: string;
};

export type FinalPayableClientSnapshot = {
  schema: "FINAL_PAYABLE_ROUNDING_V1";
  pre_rounding_total: number;
  rounding_amount: number;
  total_amount: number;
  rounding_scope: CommerceCalculationRuntimeItem["rounding_scope"];
  rounding_mode: CommerceCalculationRuntimeItem["rounding_mode"];
  rounding_increment: number;
  calculation_settings_version_id: string;
};

function toCurrencyMinor(value: number) {
  // Match the existing local money normalization used by Promotion Runtime.
  // Values are non-negative in this final-payable path, so EPSILON preserves
  // decimal half-up boundaries such as 1.005 instead of binary-float 1.004999….
  return Math.round((value + Number.EPSILON) * 100);
}

function fromCurrencyMinor(value: number) {
  return value / 100;
}

/**
 * Client-side parity implementation of Core mbos_calculate_final_payable_rounding_v1.
 * Core remains final authority; this resolver only keeps the POS critical path local-first.
 */
export function calculateFinalPayableRounding(
  amount: number,
  settings: CommerceCalculationRuntimeItem,
): FinalPayableRoundingResult {
  if (!Number.isFinite(amount) || amount < 0) throw new Error("FINAL_PAYABLE_AMOUNT_INVALID");
  if (settings.rounding_scope !== "ALL_PAYMENTS") throw new Error("ROUNDING_SCOPE_NOT_SUPPORTED_YET");
  if (!["NONE", "NEAREST", "DOWN", "UP"].includes(settings.rounding_mode)) {
    throw new Error("UNSUPPORTED_ROUNDING_MODE");
  }

  const amountMinor = toCurrencyMinor(amount);
  const incrementMinor = toCurrencyMinor(settings.rounding_increment);
  if (incrementMinor <= 0) throw new Error("ROUNDING_INCREMENT_INVALID");

  let totalMinor = amountMinor;
  if (settings.rounding_mode !== "NONE") {
    const quotient = Math.floor(amountMinor / incrementMinor);
    const remainder = amountMinor - quotient * incrementMinor;
    if (settings.rounding_mode === "DOWN") totalMinor = quotient * incrementMinor;
    if (settings.rounding_mode === "UP") totalMinor = remainder === 0 ? amountMinor : (quotient + 1) * incrementMinor;
    if (settings.rounding_mode === "NEAREST") {
      totalMinor = (remainder * 2 >= incrementMinor ? quotient + 1 : quotient) * incrementMinor;
    }
  }

  const preRoundingTotal = fromCurrencyMinor(amountMinor);
  const totalAmount = fromCurrencyMinor(totalMinor);
  return {
    preRoundingTotal,
    roundingAmount: fromCurrencyMinor(totalMinor - amountMinor),
    totalAmount,
    roundingScope: settings.rounding_scope,
    roundingMode: settings.rounding_mode,
    roundingIncrement: fromCurrencyMinor(incrementMinor),
    calculationSettingsVersionId: settings.settings_version_id,
  };
}

export function finalPayableClientSnapshot(
  result: FinalPayableRoundingResult,
): FinalPayableClientSnapshot {
  return {
    schema: "FINAL_PAYABLE_ROUNDING_V1",
    pre_rounding_total: result.preRoundingTotal,
    rounding_amount: result.roundingAmount,
    total_amount: result.totalAmount,
    rounding_scope: result.roundingScope,
    rounding_mode: result.roundingMode,
    rounding_increment: result.roundingIncrement,
    calculation_settings_version_id: result.calculationSettingsVersionId,
  };
}
